/**
 * Pure, browser-safe format helpers for the Peer Cohort feature.
 *
 * Lives in `src/shared/` so both the backend service
 * (`src/services/intelligence/peer-cohort.ts`) and the React frontend
 * (`PeerCohortHeadline.tsx`) can import the same formatters without
 * the frontend bundle pulling in `postgres` / `@/db/client.ts` via
 * the service module's import graph.
 *
 * Anything in this file MUST be pure — no DB, no fs, no Node-only
 * APIs — so it tree-shakes cleanly into the browser bundle.
 */

import type { ScoreBand } from "@/features/intelligence/types/index.ts";
import type {
  ComparablesHeadline,
  PeerCohortResponse,
  SubScoreDiffEntry,
} from "@/features/intelligence/types/peer-cohort.ts";

// ─── Raise buckets (display labels) ─────────────────────────────────

export interface RaiseBucket {
  /** Display label, e.g. "$3–10M". */
  label: string;
  /** Inclusive lower bound in USD, or null for "less than upper". */
  lowerUsd: number | null;
  /** Exclusive upper bound in USD, or null for open-ended. */
  upperUsd: number | null;
}

export const RAISE_BUCKETS: RaiseBucket[] = [
  { label: "<$1M", lowerUsd: null, upperUsd: 1_000_000 },
  { label: "$1–3M", lowerUsd: 1_000_000, upperUsd: 3_000_000 },
  { label: "$3–10M", lowerUsd: 3_000_000, upperUsd: 10_000_000 },
  { label: "$10–30M", lowerUsd: 10_000_000, upperUsd: 30_000_000 },
  { label: "$30–100M", lowerUsd: 30_000_000, upperUsd: 100_000_000 },
  { label: ">$100M", lowerUsd: 100_000_000, upperUsd: null },
];

/** Returns the display-label bucket for a raise amount, or null when 0/missing. */
export function bucketRaise(usd: number | null | undefined): RaiseBucket | null {
  if (usd == null || usd <= 0) return null;
  for (const bucket of RAISE_BUCKETS) {
    if (bucket.lowerUsd != null && usd < bucket.lowerUsd) continue;
    if (bucket.upperUsd != null && usd >= bucket.upperUsd) continue;
    return bucket;
  }
  return null;
}

// ─── Score band ─────────────────────────────────────────────────────

export function scoreToBand(score: number | null | undefined): ScoreBand | null {
  if (score == null) return null;
  if (score >= 40) return "40+";
  if (score >= 30) return "30-39";
  if (score >= 20) return "20-29";
  return "<20";
}

// ─── USD formatters ─────────────────────────────────────────────────

/**
 * Format absolute USD as "$Xm" / "$Xk" without sign. Negative inputs
 * are flipped to absolute — callers wanting a signed display should
 * use `formatSignedUsd`.
 */
export function formatAbsUsd(usd: number): string {
  const abs = Math.abs(usd);
  if (abs >= 1_000_000) return `$${(abs / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (abs >= 1_000) return `$${(abs / 1_000).toFixed(0)}k`;
  return `$${abs.toFixed(0)}`;
}

/** Format signed USD as "+$Xm" / "−$Xk". Used for neighbor diff strings. */
export function formatSignedUsd(usd: number): string {
  const abs = Math.abs(usd);
  const sign = usd < 0 ? "−" : "+";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}k`;
  return `${sign}$${abs.toFixed(0)}`;
}

export function formatResidualLabel(residualUsd: number | null): string | null {
  if (residualUsd == null) return null;
  // Residual = actual − predicted. Positive → raised more than expected (overvalued).
  if (Math.abs(residualUsd) < 100_000) return "In line with cohort"; // <$100k tolerance
  const direction = residualUsd > 0 ? "overvalued by" : "undervalued by";
  return `${direction} ~${formatAbsUsd(residualUsd)} vs cohort`;
}

// ─── Headline composition ───────────────────────────────────────────

/** Picks the largest positive sub-score delta as the project's "edge"; null when no positive. */
export function pickTopEdge(diffs: SubScoreDiffEntry[]): SubScoreDiffEntry | null {
  let best: SubScoreDiffEntry | null = null;
  for (const d of diffs) {
    if (d.delta <= 0) continue;
    if (!best || d.delta > best.delta) best = d;
  }
  return best;
}

/** Composes the headline strip text from a cohort response. */
export function composeHeadline(response: PeerCohortResponse): ComparablesHeadline | null {
  if (!response.cohort || !response.positioning) return null;
  const total = response.cohort.size + 1; // cohort excludes target; total includes
  const rankSummary = `Ranked ${ordinal(response.positioning.rankInCohort)} of ${total} in cohort`;
  const top = pickTopEdge(response.positioning.subScoreDiff);
  const topEdge = top ? `+${top.delta.toFixed(1)} ${top.key} vs cohort median` : null;
  const trajectoryLabel = response.trajectory?.growthLabel ?? null;
  return { rankSummary, topEdge, trajectoryLabel };
}

function ordinal(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n}st`;
  if (mod10 === 2 && mod100 !== 12) return `${n}nd`;
  if (mod10 === 3 && mod100 !== 13) return `${n}rd`;
  return `${n}th`;
}
